import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Header, Footer } from './Layout';
import { Loader2 } from 'lucide-react';
import { NewsWidget } from './NewsWidget';
import { publicApi } from '../utils/api';
import { LandingCmsSection } from './landing/LandingCmsSection';
import { StaticLandingFallback } from './landing/StaticLandingFallback';

export function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cmsData, setCmsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    publicApi.get('/api/cms/pages/home')
      .then(({ data }) => {
        if (alive && data?.section_data?.length > 0) setCmsData(data);
      })
      .catch((e) => {
        // No CMS home page — use static fallback.
        console.error('CMS fetch failed, using static fallback:', e);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const onCta = () => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } else {
      navigate('/register');
    }
  };

  const visibleSections = useMemo(() => {
    if (!cmsData?.section_data) return [];
    return cmsData.section_data
      .filter((s) => s.is_visible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [cmsData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000B18]">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
        <Footer />
      </div>
    );
  }

  const hasCms = cmsData && cmsData.section_data && cmsData.section_data.length > 0;
  const cmsHasHero = hasCms && cmsData.section_data.some((s) => s.section_type === 'hero' && s.is_visible !== false);

  return (
    <div className="min-h-screen bg-[#000B18]">
      <Header />
      <main className="pt-16">
        {hasCms ? (
          <>
            {/* If CMS has no hero section, prepend the static hero (only the
                hero block, not the rest of the static fallback) so the page
                always has a landing area above the fold. */}
            {!cmsHasHero && (
              <LandingCmsSection
                section={{
                  section_type: 'hero',
                  title: 'Unlimited Access. Exclusive Discounts. Endless Opportunities.',
                  subtitle: 'The Future Starts Here',
                  content: "We're building something powerful — a platform designed to give you unlimited access to services, exclusive product discounts, and endless opportunities.",
                }}
                idx={-1}
                onCta={onCta}
              />
            )}
            {visibleSections.map((section, idx) => (
              <LandingCmsSection key={section.id || idx} section={section} idx={idx} onCta={onCta} />
            ))}
            <section className="py-16 px-4">
              <div className="max-w-5xl mx-auto">
                <NewsWidget maxItems={6} />
              </div>
            </section>
          </>
        ) : (
          <>
            <StaticLandingFallback onCta={onCta} />
            <section className="py-12 sm:py-16 px-4">
              <div className="max-w-5xl mx-auto">
                <NewsWidget maxItems={6} />
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default LandingPage;
